import { useEffect, useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send, Paperclip, X, Download, FileText, ShieldCheck } from "lucide-react";
import { STATUS_META, PRIORITY_META, fmtDateTime, fmtSize, uploadSupportFile, supportFileUrl, fmtTicketNo } from "./supportUtils";

// Vista dettaglio ticket lato ADMIN: colonna "Informazioni ticket" + colonna "Conversazione".
export const SupportTicketDetailAdmin = ({ ticketId, onBack, onChanged }) => {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [atts, setAtts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/support/tickets/${ticketId}`); setTicket(r.data); onChanged?.(); }
    catch { toast.error("Impossibile caricare il ticket"); }
    finally { setLoading(false); }
  }, [ticketId, onChanged]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [ticket?.messages?.length]);

  const onAttach = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: troppo grande (max 10 MB)`); continue; }
      try { const rec = await uploadSupportFile(f); setAtts((p) => [...p, rec]); } catch { toast.error(`Caricamento di ${f.name} non riuscito`); }
    }
    setUploading(false);
  };

  const send = async () => {
    if (!body.trim() && !atts.length) return;
    setSending(true);
    try {
      const r = await api.post(`/support/tickets/${ticketId}/messages`, { body: body.trim(), attachments: atts });
      setTicket(r.data.ticket); setBody(""); setAtts([]); onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Invio non riuscito"); }
    finally { setSending(false); }
  };

  const onKey = (e) => { if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && ticket?.can_reply !== false) send(); };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!ticket) return null;

  const st = STATUS_META[ticket.status] || STATUS_META.aperto;
  const pr = PRIORITY_META[ticket.priority] || PRIORITY_META.media;
  const code = "#" + String(ticket.id || "").replace(/-/g, "").slice(0, 6).toUpperCase();
  const messages = ticket.messages || [];
  const description = messages[0];
  const convo = messages.slice(1); // il primo messaggio è la "Descrizione"
  const canReply = ticket.can_reply !== false;

  return (
    <div data-testid="support-conversation" className="space-y-5">
      {/* Intestazione */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Assistenza</div>
          <h2 className="font-head text-3xl font-extrabold tracking-tighter mt-0.5">{code}</h2>
          <p className="text-sm text-muted-foreground mt-1">Consulta e aggiorna il ticket di assistenza selezionato.</p>
        </div>
        {onBack && <button onClick={onBack} data-testid="support-back" className="text-sm font-medium text-muted-foreground hover:text-foreground border-b border-transparent hover:border-current">Lista</button>}
      </div>

      {/* Card riepilogo */}
      <div className="bg-card border border-border rounded-2xl p-6">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-xs font-bold px-3 py-1 rounded-full bg-foreground text-background">{code}</span>
          <span className={`text-xs font-semibold px-3 py-1 rounded-full ${st.cls}`} data-testid="support-status-badge">{st.label}</span>
          <span className={`text-xs font-semibold px-3 py-1 rounded-full ${pr.cls}`}>Priorità: {pr.label}</span>
        </div>
        <h3 className="font-head text-2xl font-bold tracking-tight flex items-center gap-2">
          {ticket.ticket_no != null && <span className="text-xs font-mono font-bold px-2.5 py-1 rounded-full bg-slate-800 text-white">{fmtTicketNo(ticket.ticket_no)}</span>}
          {ticket.subject}
        </h3>
        <p className="text-sm text-muted-foreground mt-2">
          Apertura: {fmtDateTime(ticket.created_at)} &nbsp;•&nbsp; Ultimo aggiornamento: {fmtDateTime(ticket.updated_at || ticket.last_message_at)}
        </p>
      </div>

      {/* Due colonne */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,360px)_1fr] gap-5">
        {/* Informazioni ticket */}
        <div className="bg-card border border-border rounded-2xl p-6">
          <h4 className="font-head text-xl font-bold tracking-tight mb-4">Informazioni ticket</h4>
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between gap-3"><dt className="font-semibold">Categoria</dt><dd className="text-muted-foreground text-right">{ticket.category || "—"}</dd></div>
            <div className="flex justify-between gap-3"><dt className="font-semibold">Priorità</dt><dd className="text-muted-foreground text-right">{pr.label}</dd></div>
            <div className="flex justify-between gap-3"><dt className="font-semibold">Stato</dt><dd className="text-muted-foreground text-right">{st.label}</dd></div>
          </dl>
          <div className="mt-4">
            <div className="font-semibold text-sm mb-1">Descrizione</div>
            <div className="rounded-lg border border-border bg-secondary/40 px-3 py-2.5 text-sm text-muted-foreground whitespace-pre-wrap break-words min-h-[44px]" data-testid="support-description">
              {description?.body || "—"}
            </div>
          </div>
          <div className="mt-4">
            <div className="font-semibold text-sm mb-1">Allegati</div>
            {(description?.attachments || []).length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {description.attachments.map((a) => (
                  <a key={a.file_id} href={supportFileUrl(a.file_id)} target="_blank" rel="noopener noreferrer" download
                    data-testid={`support-att-${a.file_id}`}
                    className="inline-flex items-center gap-1.5 text-[11px] rounded-lg px-2 py-1 bg-background border border-border hover:bg-secondary">
                    <FileText className="h-3 w-3 shrink-0" /><span className="truncate max-w-[140px]">{a.filename}</span>
                    {a.size ? <span className="opacity-70">· {fmtSize(a.size)}</span> : null}<Download className="h-3 w-3 shrink-0" />
                  </a>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">Nessun allegato disponibile.</p>}
          </div>
        </div>

        {/* Conversazione */}
        <div className="bg-card border border-border rounded-2xl p-6 flex flex-col">
          <h4 className="font-head text-xl font-bold tracking-tight">Conversazione</h4>
          <p className="text-sm text-muted-foreground mt-0.5 mb-4">Usa la chat per rispondere o aggiungere dettagli al ticket.</p>

          <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-4 min-h-[180px] max-h-[45vh]">
            {convo.length === 0 ? (
              <div className="h-full min-h-[160px] flex items-center justify-center rounded-lg bg-secondary/30 text-sm text-muted-foreground text-center px-6">
                Nessun commento presente. Inizia la conversazione con un messaggio.
              </div>
            ) : convo.map((m) => {
              const mine = m.author_role === "admin";
              const fromSuper = m.author_role === "super_admin";
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`support-msg-${m.id}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${mine ? "bg-[#007BFF] text-white" : "bg-secondary text-foreground"}`}>
                    <div className={`text-[10px] font-semibold mb-1 flex items-center gap-1 ${mine ? "text-white/80" : "text-muted-foreground"}`}>
                      {fromSuper && <ShieldCheck className="h-3 w-3" />}
                      {fromSuper ? "Assistenza Sedio" : (m.author_name || "Tu")} · {fmtDateTime(m.created_at)}
                    </div>
                    {m.body && <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>}
                    {(m.attachments || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {m.attachments.map((a) => (
                          <a key={a.file_id} href={supportFileUrl(a.file_id)} target="_blank" rel="noopener noreferrer" download
                            className={`inline-flex items-center gap-1.5 text-[11px] rounded-lg px-2 py-1 ${mine ? "bg-white/20 hover:bg-white/30 text-white" : "bg-background border border-border hover:bg-secondary"}`}>
                            <FileText className="h-3 w-3 shrink-0" /><span className="truncate max-w-[140px]">{a.filename}</span><Download className="h-3 w-3 shrink-0" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="pt-4 mt-2 border-t border-border">
            {ticket.closed ? (
              <div data-testid="support-closed-note" className="mb-2 flex items-center gap-2 text-xs text-muted-foreground bg-secondary/60 border border-border rounded-lg px-3 py-2">
                <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
                Questo ticket è {ticket.status === "chiuso" ? "chiuso" : "risolto"}: non è più possibile inviare messaggi.
              </div>
            ) : !canReply && (
              <div data-testid="support-wait-turn" className="mb-2 flex items-center gap-2 text-xs text-muted-foreground bg-secondary/60 border border-border rounded-lg px-3 py-2">
                <Loader2 className="h-3.5 w-3.5 shrink-0" />
                Hai già inviato un messaggio. Attendi la risposta dell'Assistenza Sedio prima di scriverne un altro.
              </div>
            )}
            {atts.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2" data-testid="support-reply-atts">
                {atts.map((a) => (
                  <span key={a.file_id} className="inline-flex items-center gap-1.5 text-xs bg-[#007BFF]/10 text-[#0056B3] border border-[#007BFF]/30 rounded-lg px-2 py-1">
                    <Paperclip className="h-3 w-3" /><span className="truncate max-w-[140px]">{a.filename}</span>
                    <button onClick={() => setAtts((p) => p.filter((x) => x.file_id !== a.file_id))} className="hover:bg-[#007BFF]/20 rounded-full p-0.5"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}
            <Textarea data-testid="support-reply-input" value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={onKey} disabled={!canReply}
              placeholder={canReply ? "Scrivi qui il tuo messaggio per l'assistenza." : "In attesa della risposta dell'Assistenza Sedio…"} className="min-h-[90px] text-sm disabled:opacity-60" />
            <div className="mt-3">
              <div className="text-sm font-semibold mb-1">Allega file (facoltativi)</div>
              <div className="flex items-center gap-2 flex-wrap">
                <input ref={fileRef} type="file" multiple className="hidden" data-testid="support-reply-file" onChange={onAttach} />
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading || !canReply} className="rounded-md">
                  {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Paperclip className="h-4 w-4 mr-2" />}Scegli file
                </Button>
                <span className="text-xs text-muted-foreground">{atts.length ? `${atts.length} file selezionati` : "nessun file selezionato"}</span>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 mt-4">
              <span className="text-xs text-muted-foreground">Suggerimento: invia rapidamente con Ctrl+Invio (Cmd+Invio su Mac).</span>
              <Button data-testid="support-send-btn" onClick={send} disabled={sending || !canReply || (!body.trim() && !atts.length)} className="bg-[#007BFF] hover:bg-[#0056B3] rounded-md">
                {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Invia messaggio
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
